import { getSuiteSlug, priceForSuite } from "src/utils/suites";
import { WindowLocation } from "@reach/router";
import useLiveSanityContent, {
  UseLiveSanityContentResponse
} from "src/utils/useLiveSanityContent";
import { useLocationSpecificOffers } from "components/useLocationSpecificOffers/useLocationSpecificOffers";
import useCruisePrice from "src/utils/useCruisePrice";
import { LocationSpecificOffer } from "components/useLocationSpecificOffers/types";
import { CruisePriceFrom } from "types/api/suite_price";
import { buildCruiseName } from "src/utils/cruise";
import { FetchApiStatus } from "../../utils/useFetchApi";
import { SanityImageType } from "@silversea-ssc/ui-components-react";
import { Language } from "src/utils/localization/languages/types";

type RemoteCruiseCardContent_Ship = {
  _id: string;
  name: string;
  suites: RemoteCruiseCardContent_Suite[];
};

export type RemoteCruiseCardContent_Suite = {
  codes: string[];
  category: {
    name: {
      localized: string;
    };
  };
};

export type RemoteCruiseCardContent = {
  cruise: {
    _id: string;
    cardImage: { asset: { _ref: string } };
    card?: { image?: { asset: { _ref: string } } };
    data?: {
      departurePort?: {
        data?: {
          name?: {
            localized?: string;
          };
        };
      };
      arrivalPort?: {
        data?: {
          name?: {
            localized?: string;
          };
        };
      };
      departureDate: string;
      days: number;
      ship: RemoteCruiseCardContent_Ship;
    };
  };
  specialVoyage: {
    _id: string;
    cardImage: { asset: { _ref: string } };
    card?: { image?: { asset: { _ref: string } } };
    data?: {
      departurePort?: {
        data?: {
          name?: {
            localized?: string;
          };
        };
      };
      arrivalPort?: {
        data?: {
          name?: {
            localized?: string;
          };
        };
      };
      departureDate: string;
      days: number;
      ship: RemoteCruiseCardContent_Ship;
    };
  };
};

const shipQuery = `
ship->{
  _id,
  name,
  suites[]{
    codes,
    category->{
      name{"localized": en}
    }
  }
}
`;

export const useCruiseCard = (
  cruiseCode?: string
): UseLiveSanityContentResponse<RemoteCruiseCardContent> => {
  const query = cruiseCode
    ? `
    {
      "cruise": *[_type == "cruise" && data._ref == (*[_type=="cruiseData" && cruiseCode == "${cruiseCode}"][0]{_id}._id)]
      {
        _id,
        data -> {
          departureDate,
          departurePort->{data->{name{"localized": en}}},
          arrivalPort->{data->{name{"localized": en}}},
          days,
        ${shipQuery}
        },
        cardImage{asset}
      }[0],

      "specialVoyage": *[_type == "specialVoyage" && data._ref == (*[_type=="specialVoyageData" && cruiseCode == "${cruiseCode}"][0]{_id}._id)]
      {
        _id,
        data -> {
          departureDate,
          departurePort->{data->{name{"localized": en}}},
          arrivalPort->{data->{name{"localized": en}}},
          days,
        ${shipQuery}
        },
        card {
          image{asset}
        }
      }[0]
    }
  `
    : undefined;

  return useLiveSanityContent<RemoteCruiseCardContent>(query);
};

export const useCruiseCardPrice = (
  cruiseCode: string | undefined,
  suite: RemoteCruiseCardContent_Suite | undefined,
  location: WindowLocation,
  language: Language
): {
  status: FetchApiStatus;
  price?: CruisePriceFrom;
  offers: LocationSpecificOffer[];
} => {
  const { status, data: cruisePrice } = useCruisePrice(
    cruiseCode,
    location,
    language
  );

  const suitePrice =
    suite && priceForSuite(cruisePrice?.suitePrices, suite.codes);
  const cruiseExclusiveOfferList = cruisePrice?.exclusiveOffers || [];
  const locationSpecificOffers = useLocationSpecificOffers(
    cruiseExclusiveOfferList
  );

  return {
    status,
    price: suitePrice?.priceFrom || cruisePrice?.priceFrom,
    offers: locationSpecificOffers
  };
};

export const getSuite = <T extends { codes: string[] }>(
  shipId: string,
  suiteSlug: string,
  suites: T[]
): T | undefined => {
  if (!suiteSlug) return undefined;
  return suites.find((s) =>
    s.codes.some((code) => getSuiteSlug(shipId, code) == suiteSlug)
  );
};

export const getCardSummaryContent = (
  cruise?: RemoteCruiseCardContent["cruise"],
  specialVoyage?: RemoteCruiseCardContent["specialVoyage"]
): { title: string; image?: SanityImageType } => {
  const cruiseOrSpecialVoyage = cruise || specialVoyage;
  const title = cruiseOrSpecialVoyage
    ? buildCruiseName(cruiseOrSpecialVoyage)
    : "";
  const imageRef =
    cruise?.cardImage?.asset?._ref || specialVoyage?.card?.image?.asset?._ref;
  const image = imageRef
    ? {
        asset: {
          _id: imageRef
        }
      }
    : undefined;

  return { title, image };
};

export const priceToNumber = (price: CruisePriceFrom): number | undefined => {
  if (!price || price.type == "waitlist") return undefined;
  return price.amount;
};
